import { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, Link2, Loader2, Car, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import ReactMarkdown from 'react-markdown';
import { API } from '../lib/apiBase';

const SUGGESTIONS = [
  { label: 'Toyota Corolla 2023', icon: Car },
  { label: 'BMW 3 Serisi 2020 değer kaybı', icon: Car },
  { label: 'Renault Clio yaygın sorunlar', icon: Car },
  { label: 'sahibinden.com ilan linki analizi', icon: Link2 },
];

const Landing = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const sendMessage = async (messageText) => {
    const text = (messageText || input).trim();
    if (!text || loading) return;

    const userMsgId = `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const asstMsgId = `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setMessages((prev) => [
      ...prev,
      { id: userMsgId, role: 'user', content: text },
      { id: asstMsgId, role: 'assistant', content: '' },
    ]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by \n\n
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr);

            if (event.type === 'session' && !sessionId) {
              setSessionId(event.session_id);
            } else if (event.type === 'delta') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === asstMsgId ? { ...m, content: m.content + event.content } : m
                )
              );
            } else if (event.type === 'error') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === asstMsgId ? { ...m, content: `⚠️ ${event.message || 'Bir hata oluştu'}` } : m
                )
              );
            }
          } catch (e) {
            console.error('SSE parse error:', e, jsonStr);
          }
        }
      }
    } catch (error) {
      const errorMsg = error.message || 'Bir hata oluştu. Lütfen tekrar deneyin.';
      setMessages((prev) =>
        prev.map((m) =>
          m.id === asstMsgId ? { ...m, content: `⚠️ ${errorMsg}` } : m
        )
      );
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetChat = () => {
    setMessages([]);
    setSessionId(null);
    setInput('');
  };

  const hasMessages = messages.length > 0;

  return (
    <div className="min-h-[calc(100vh-65px)] bg-background flex flex-col" data-testid="landing-page">
      {/* Hero / Empty state */}
      {!hasMessages && (
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
          <div className="max-w-3xl w-full text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-border bg-secondary/50 mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                Tarafsız Araç Danışmanın
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black tracking-tighter mb-4" data-testid="hero-title">
              Volt Usta'ya Sor,
              <br />
              <span className="text-primary">tarafsız değerlendirme al.</span>
            </h1>

            <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-2xl mx-auto">
              Marka/model yaz veya sahibinden.com ilan linkini yapıştır. Volt Usta sana fiyat,
              değer kaybı, yaygın sorunlar ve çözümleri tarafsız bir bakış açısıyla değerlendirsin.
            </p>

            {/* Main chat input */}
            <div className="w-full max-w-2xl mx-auto mb-6">
              <div className="border border-border bg-card focus-within:border-primary transition-colors" data-testid="chat-input-container">
                <Textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Örn: BMW 3 Serisi 2020 alınır mı? veya https://www.sahibinden.com/ilan/..."
                  rows={3}
                  className="border-0 rounded-none resize-none focus-visible:ring-0 text-base p-4 font-body"
                  data-testid="chat-input"
                />
                <div className="flex items-center justify-between px-4 py-2 border-t border-border bg-secondary/30">
                  <p className="text-xs text-muted-foreground">
                    Enter ile gönder · Shift+Enter ile yeni satır
                  </p>
                  <Button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || loading}
                    className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="send-button"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        Sor
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Suggestion chips */}
            <div className="flex flex-wrap items-center justify-center gap-2 max-w-2xl mx-auto" data-testid="suggestions">
              {SUGGESTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.label}
                    onClick={() => sendMessage(s.label)}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-border hover:border-primary hover:bg-secondary/50 text-sm transition-colors"
                    data-testid={`suggestion-${s.label}`}
                  >
                    <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Chat conversation view */}
      {hasMessages && (
        <div className="flex-1 flex flex-col max-w-3xl w-full mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
            <h2 className="text-lg font-heading font-semibold tracking-tight">Volt Usta Sohbet</h2>
            <Button
              onClick={resetChat}
              variant="ghost"
              size="sm"
              className="rounded-sm text-muted-foreground hover:text-foreground"
              data-testid="reset-chat-button"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Yeni Sohbet
            </Button>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto space-y-6 mb-4 pb-4"
            style={{ maxHeight: 'calc(100vh - 280px)' }}
            data-testid="messages-container"
          >
            {messages.map((msg, idx) => (
              <div
                key={msg.id}
                className={msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
                data-testid={`message-${msg.role}-${idx}`}
              >
                <div
                  className={
                    msg.role === 'user'
                      ? 'max-w-[85%] bg-primary text-primary-foreground px-4 py-3 rounded-sm'
                      : 'max-w-[95%] border-l-2 border-primary pl-4 py-1 prose prose-sm prose-neutral max-w-none'
                  }
                >
                  {msg.role === 'user' ? (
                    <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                  ) : msg.content === '' ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-1" data-testid="loading-indicator">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Volt Usta düşünüyor...</span>
                    </div>
                  ) : (
                    <div className="text-sm leading-relaxed">
                      <ReactMarkdown
                        components={{
                          h1: ({ children }) => <h3 className="text-lg font-heading font-bold mt-4 mb-2">{children}</h3>,
                          h2: ({ children }) => <h3 className="text-base font-heading font-bold mt-4 mb-2">{children}</h3>,
                          h3: ({ children }) => <h4 className="text-base font-heading font-semibold mt-3 mb-1.5">{children}</h4>,
                          p: ({ children }) => <p className="mb-2">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-2 space-y-1">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-outside ml-5 mb-2 space-y-1">{children}</ol>,
                          li: ({ children }) => <li>{children}</li>,
                          strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                          code: ({ children }) => <code className="font-mono text-xs bg-secondary px-1 py-0.5">{children}</code>,
                          a: ({ children, href }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">{children}</a>,
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Sticky input */}
          <div className="border border-border bg-card focus-within:border-primary transition-colors">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Bir soru sor veya sahibinden.com linki yapıştır..."
              rows={2}
              className="border-0 rounded-none resize-none focus-visible:ring-0 text-sm p-3 font-body"
              data-testid="chat-input-bottom"
            />
            <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-secondary/30">
              <p className="text-xs text-muted-foreground">Enter ile gönder</p>
              <Button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                size="sm"
                className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="send-button-bottom"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
